import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { convertPdfToImages } from './pdfToImage';
import { extractTextWithTesseractOCR } from './tesseractOcrService';

// Настройка worker для PDF.js v5.x
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * Извлекает текст из TXT файла
 * @param {File} file - TXT файл
 * @returns {Promise<string>} - Извлеченный текст
 */
export const extractTextFromTXT = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
};

/**
 * Извлекает текст из PDF файла
 * @param {File} file - PDF файл
 * @returns {Promise<string>} - Извлеченный текст
 */
export const extractTextFromPDF = async (file) => {
  try {
    console.log('Starting PDF text extraction...');
    const arrayBuffer = await file.arrayBuffer();
    console.log('ArrayBuffer created, size:', arrayBuffer.byteLength);

    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    console.log('PDF loaded, pages:', pdf.numPages);

    let fullText = '';

    // Извлекаем текст со всех страниц
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      console.log(`Page ${pageNum} - textContent.items:`, textContent.items.length);

      // Логируем первый item для диагностики
      if (textContent.items.length > 0) {
        console.log(`Page ${pageNum} - first item structure:`, textContent.items[0]);
        console.log(`Page ${pageNum} - first item keys:`, Object.keys(textContent.items[0]));
      }

      // Извлекаем текст из items
      const pageText = textContent.items
        .map(item => {
          // В PDF.js 5.x может быть item.str или item.text
          const text = item.str || item.text || '';
          return text;
        })
        .filter(text => text.trim().length > 0) // Удаляем пустые строки
        .join(' ');

      console.log(`Page ${pageNum} - extracted text length:`, pageText.length);
      if (pageText.length > 0) {
        console.log(`Page ${pageNum} - first 100 chars:`, pageText.substring(0, 100));
      }

      fullText += pageText + '\n';
    }

    const pdfJsText = fullText.trim();
    console.log('PDF.js extraction completed, length:', pdfJsText.length);

    // Крок 2: ЗАВЖДИ запускаємо Tesseract.js OCR (незалежно від того чи знайшов PDF.js текст)
    console.log('Starting Tesseract.js OCR...');
    let tesseractText = '';

    try {
      const tesseractResult = await extractTextWithTesseractOCR(file, 3);
      tesseractText = tesseractResult || '';
      console.log('Tesseract.js OCR completed, length:', tesseractText.length);
    } catch (error) {
      console.warn('Tesseract.js OCR failed, continuing without it:', error.message);
    }

    // Крок 3: Об'єднуємо результати з обох джерел
    const textParts = [pdfJsText, tesseractText]
      .filter(t => t && t.trim().length > 0);

    if (textParts.length === 0) {
      // Нічого не витягнуто локально - fallback на Gemini Vision
      console.log('No text extracted locally (PDF.js + Tesseract.js), converting to images for Gemini Vision...');
      const images = await convertPdfToImages(file, 3);
      return {
        isImages: true,
        images: images
      };
    }

    // Об'єднуємо всі витягнуті тексти
    const combinedText = textParts.join('\n\n--- OCR SECTION ---\n\n');

    console.log('Combined extraction results:');
    console.log('- PDF.js text length:', pdfJsText.length);
    console.log('- Tesseract.js text length:', tesseractText.length);
    console.log('- Total combined length:', combinedText.length);

    return combinedText;
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });

    // Более детальные сообщения об ошибках
    if (error.message?.includes('Invalid PDF')) {
      throw new Error('Invalid or corrupted PDF file');
    } else if (error.message?.includes('password')) {
      throw new Error('PDF is password protected');
    } else if (error.message?.includes('images')) {
      throw new Error(error.message);
    } else {
      throw new Error(`Failed to extract text from PDF: ${error.message || 'Unknown error'}`);
    }
  }
};

/**
 * Извлекает текст из старого DOC файла (частичная поддержка)
 * @param {File} file - DOC файл
 * @returns {Promise<string>} - Извлеченный текст (может содержать артефакты)
 */
export const extractTextFromDOC = async (file) => {
  try {
    console.log('Attempting to extract text from old .doc format...');

    // Пробуем прочитать как текст (старый .doc иногда содержит читаемый текст)
    const text = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        // Удаляем большинство бинарных символов, оставляем только читаемый текст
        const cleanText = result
          .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F-\x9F]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        resolve(cleanText);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file, 'UTF-8');
    });

    if (text.length < 50) {
      throw new Error('Not enough text extracted from .doc file');
    }

    console.log(`Extracted ${text.length} characters from .doc file (may contain artifacts)`);
    return text;
  } catch (error) {
    console.error('Error extracting text from DOC:', error);
    throw new Error('Old .DOC format could not be processed. Please convert to .DOCX or paste text manually.');
  }
};

/**
 * Извлекает текст из DOCX файла
 * @param {File} file - DOCX файл
 * @returns {Promise<string>} - Извлеченный текст
 */
export const extractTextFromDOCX = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value.trim();
  } catch (error) {
    console.error('Error extracting text from DOCX:', error);
    throw new Error('Failed to extract text from DOCX file. Please try another file or paste text manually.');
  }
};

/**
 * Определяет тип файла и извлекает текст
 * @param {File} file - Файл для обработки
 * @returns {Promise<string>} - Извлеченный текст
 */
export const extractTextFromFile = async (file) => {
  if (!file) {
    throw new Error('No file provided');
  }

  const fileExtension = file.name.split('.').pop().toLowerCase();

  switch (fileExtension) {
    case 'txt':
      return await extractTextFromTXT(file);

    case 'pdf':
      return await extractTextFromPDF(file);

    case 'doc':
      return await extractTextFromDOC(file);

    case 'docx':
      return await extractTextFromDOCX(file);

    default:
      throw new Error(`Unsupported file format: ${fileExtension}. Please use TXT, PDF, DOC, or DOCX files.`);
  }
};

/**
 * Валидация размера файла (максимум 5MB)
 * @param {File} file - Файл для проверки
 * @returns {boolean} - true если размер допустимый
 */
export const validateFileSize = (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
  }
  return true;
};
